var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import React from 'react';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import { BreakpointsContext } from '@cvent/nucleus-layout';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { toMediaQuery, toMaxMediaQuery, carinaFontReset } from './styles';
var FORM_HEADING_SECTION_WIDTH = 200;
export var isFormSectionChild = function (child) {
    return (!!child &&
        !!child.type &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        child.type.layoutId === 'FormSection');
};
/**
 * Sections have **headings** and **content**. If `stack`-ed or below the mobile
 * threshold, the heading area is above the content. Otherwise, the heading area
 * is a fixed width column (200px) next to the content (rtl-aware).
 */
var FormSection = /** @class */ (function (_super) {
    __extends(FormSection, _super);
    function FormSection() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.getStackBreakpoint = function () {
            var stackBreakpoint = _this.props.stackBreakpoint;
            if (stackBreakpoint) {
                return stackBreakpoint;
            }
            return _this.context.m;
        };
        _this.renderHeader = function (theme) { return (_jsx("div", __assign({ css: [
                getHeaderStyles(theme, _this.props, _this.getStackBreakpoint()),
                carinaFontReset(theme)
            ] }, { children: _jsx("h3", __assign({ css: {
                    fontSize: theme.font.base.size.h3,
                    lineHeight: theme.font.lineHeight.heading,
                    fontWeight: theme.font.base.weight.regular,
                    fontFamily: theme.font.base.family,
                    color: theme.font.color.base
                } }, { children: _this.props.heading }), void 0) }), void 0)); };
        _this.renderContent = function (theme) { return (_jsx("div", __assign({ css: getContentStyles(theme, _this.props, _this.getStackBreakpoint()) }, { children: _this.renderRowsWithSharedProps(_this.props) }), void 0)); };
        _this.renderRowsWithSharedProps = function (_a) {
            var children = _a.children, isRtl = _a.isRtl;
            return React.Children.map(children, function (element) {
                return React.cloneElement(element, { isRtl: isRtl });
            });
        };
        return _this;
    }
    FormSection.prototype.componentDidUpdate = function () {
        if (process.env.NODE_ENV === 'development' && !this.props.heading) {
            // eslint-disable-next-line no-console
            console.warn("You are passing an empty value to FormSection's `heading` prop. This currently renders an empty container with padding and margin present. To remove the heading element entirely, please use the `hideHeading` prop. Future versions of FormSection will automatically remove the entire heading element if its value is empty.");
        }
    };
    Object.defineProperty(FormSection, "layoutId", {
        get: function () {
            return FormSection._layoutId;
        },
        enumerable: false,
        configurable: true
    });
    FormSection.prototype.render = function () {
        var _this = this;
        var _a = this.props, testID = _a.testID, accessibilityLabel = _a.accessibilityLabel, _stackBreakpoint = _a.stackBreakpoint, _heading = _a.heading, hideHeading = _a.hideHeading, _isRtl = _a.isRtl, _stack = _a.stack, 
        // @ts-expect-error css does not exist in the type but className is filtered out here for JS purposes
        _css = _a.css, 
        // @ts-expect-error style does not exist in the type but className is filtered out here for JS purposes
        _style = _a.style, 
        // @ts-expect-error className does not exist in the type but className is filtered out here for JS purposes
        _className = _a.className, propDrop = __rest(_a, ["testID", "accessibilityLabel", "stackBreakpoint", "heading", "hideHeading", "isRtl", "stack", "css", "style", "className"]);
        return (_jsx(ThemeConsumer, { children: function (theme) { return (_jsxs("div", __assign({}, propDrop, injectTestId(testID), { css: getContainerStyles(theme, _this.props, _this.getStackBreakpoint()), "aria-label": accessibilityLabel }, { children: [!hideHeading && _this.renderHeader(theme), _this.renderContent(theme)] }), void 0)); } }, void 0));
    };
    FormSection._layoutId = 'FormSection';
    FormSection.contextType = BreakpointsContext;
    return FormSection;
}(React.Component));
export default FormSection;
function getContainerStyles(theme, _a, stackBreakpoint) {
    var isRtl = _a.isRtl, stack = _a.stack;
    return __assign({ display: 'flex', flexDirection: 'column' }, toMediaQuery(stackBreakpoint, {
        // eslint-disable-next-line
        flexDirection: stack ? 'column' : isRtl ? 'row-reverse' : 'row',
        borderBottomWidth: theme.size.border.base,
        borderBottomColor: theme.borderColor.base,
        borderBottomStyle: 'solid',
        ':last-child': {
            borderColor: 'transparent'
        }
    }));
}
function getHeaderStyles(theme, _a, stackBreakpoint) {
    var isRtl = _a.isRtl, stack = _a.stack;
    return __assign({ color: theme.font.color.base, direction: isRtl ? 'rtl' : 'ltr' }, toMediaQuery(stackBreakpoint, stack ? {} : { width: FORM_HEADING_SECTION_WIDTH }));
}
function getContentStyles(theme, _a, stackBreakpoint) {
    var _b;
    var isRtl = _a.isRtl, stack = _a.stack, hideHeading = _a.hideHeading;
    return __assign(__assign({ flex: stack ? '1 0 auto' : '1 0 0%', paddingTop: 16, paddingBottom: 16 }, toMediaQuery(stackBreakpoint, (_b = {},
        _b[isRtl ? 'paddingRight' : 'paddingLeft'] = stack || hideHeading ? 0 : 24,
        _b))), toMaxMediaQuery(stackBreakpoint, {
        flex: '1 0 auto' // IE11
    }));
}
